import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import EditableSection from '../../components/EditableSection';
import { useEditableDraft } from '../../hooks/useEditableDraft';
import { useUpdateLegalTextsMutation } from '../../api/account/accountApiSlice';
import { TenantLegalTexts } from '../../api/account/accountApiTypes';
import { enqueueSuccessSnackbar } from '../../action/snackbarCommands';
import { shownLegalTexts } from './legalTextsVisibility';

type LegalTextsSectionProps = {
    readOnly: boolean;
    /** Stored overrides of the tenant IN VIEW — passed in, never re-queried
     *  (#1334). null = none saved, so every field shows kolula's default. */
    legalTexts: TenantLegalTexts | null;
};

type FieldKey = keyof TenantLegalTexts;

/**
 * kolula's default wording, shown as the placeholder so a tenant can see what
 * it is replacing and what clearing a field restores.
 *
 * MIRROR (display only): the authoritative copies live in
 * `backend/src/core/lib/email/postpayBlocks.ts`, which is what actually renders
 * the mails. This frontend cannot import from that backend, so the strings are
 * restated here and say so. They are placeholders — if they drift, a tenant
 * sees a stale preview of a default, never a wrong mail. Change one, change the
 * other.
 */
const KOLULA_DEFAULTS: Record<FieldKey, string> = {
    paymentTermsDe:
        'Die Zahlung erfolgt nach der Nutzung auf Rechnung, zahlbar innerhalb von 14 Tagen nach Rechnungsdatum.',
    paymentTermsEn:
        'Payment is made after use, by invoice, due within 14 days of the invoice date.',
    noWithdrawalDe:
        'Für Buchungen von Unterkünften und Räumen zu einem festen Termin besteht kein ' +
        'gesetzliches Widerrufsrecht (§ 312g Abs. 2 Nr. 9 BGB). Es gelten die ' +
        'Stornierungsbedingungen aus unseren Allgemeinen Geschäftsbedingungen.',
    noWithdrawalEn:
        'Bookings of accommodation and rooms for a specific date are exempt from the ' +
        'statutory right of withdrawal (Section 312g(2) no. 9 German Civil Code). The ' +
        'cancellation terms in our terms and conditions apply.',
    agbNoteDe: 'Es gelten unsere Allgemeinen Geschäftsbedingungen.',
    agbNoteEn: 'Our terms and conditions apply.',
};

/** Label key per text, rendered once with a DE and an EN field beneath it. */
const TEXTS: { labelKey: string, hintKey: string, de: FieldKey, en: FieldKey }[] = [
    {
        labelKey: 'account.legal_texts.payment_terms',
        hintKey: 'account.legal_texts.payment_terms_hint',
        de: 'paymentTermsDe',
        en: 'paymentTermsEn',
    },
    {
        labelKey: 'account.legal_texts.no_withdrawal',
        hintKey: 'account.legal_texts.no_withdrawal_hint',
        de: 'noWithdrawalDe',
        en: 'noWithdrawalEn',
    },
    {
        labelKey: 'account.legal_texts.agb_note',
        hintKey: 'account.legal_texts.agb_note_hint',
        de: 'agbNoteDe',
        en: 'agbNoteEn',
    },
];

const asForm = (stored: TenantLegalTexts | null): Record<FieldKey, string> =>
    Object.keys(KOLULA_DEFAULTS).reduce((acc, key) => {
        acc[key as FieldKey] = stored?.[key as FieldKey] ?? '';
        return acc;
    }, {} as Record<FieldKey, string>);

/**
 * The postpay legal texts a tenant may replace with its own — the D1 package
 * (#1703, spec §10 item 5).
 *
 * It sits in Account beside the legal notice because ADR-0005 puts the
 * tenant's legal identity here ("Stays in Account … Legal notice"). These are
 * contract terms the TENANT is liable for; kolula only supplies defaults, which
 * is why the disclaimer is at the top rather than buried at the bottom.
 *
 * Blank means "use kolula's default", never an empty term — the backend applies
 * the same rule, so a cleared field restores the default rather than dropping a
 * sentence out of a contract.
 *
 * The CTA label is deliberately not editable: "Kostenpflichtig buchen" / "Book
 * for a fee" is the § 312j BGB button wording, not a matter of taste.
 */
const LegalTextsSection = ({ readOnly, legalTexts }: LegalTextsSectionProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [updateLegalTexts, { isLoading: saving }] = useUpdateLegalTextsMutation();
    // One editable unit (design-decisions › Bearbeiten und Speichern): the view
    // is derived from the stored texts on every render, "Bearbeiten" opens a
    // draft of them. A refetch another section triggers therefore cannot
    // overwrite what the user is typing.
    const draft = useEditableDraft<Record<FieldKey, string>>(asForm(legalTexts));
    const form = draft.value;
    const [error, setError] = useState<string | null>(null);

    // AccountPage remounts this card per tenant; a change of the STORED texts
    // under the same tenant (e.g. saved elsewhere) also drops an open draft
    // rather than saving over it. Keyed on the content,
    // not the object: RTK hands back a new reference on every refetch.
    const storedKey = JSON.stringify(legalTexts ?? {});
    const { cancel } = draft;
    useEffect(() => {
        cancel();
        setError(null);
    }, [storedKey, cancel]);

    const save = async () => {
        if (saving || !draft.isDirty) return;
        setError(null);
        try {
            await updateLegalTexts(form).unwrap();
            dispatch(
                enqueueSuccessSnackbar({ message: t('account.saved'), autoHideDuration: 3000 }),
            );
            draft.finish();
        } catch (e) {
            const message = (e as { data?: { message?: string } })?.data?.message;
            setError(message ?? t('account.legal_texts.save_failed'));
        }
    };

    const field = (key: FieldKey, language: 'de' | 'en', locked: boolean) => (
        <div className={'flex flex-col w-full'}>
            <label className={'text-xs text-foreground/60 mb-1'} htmlFor={`legal-text-${key}`}>
                {t(`account.legal_texts.language_${language}`)}
            </label>
            <textarea
                id={`legal-text-${key}`}
                className={
                    'w-full border border-foreground/15 rounded-[5px] p-2 text-sm text-foreground min-h-[72px]'
                    + ' bg-transparent disabled:bg-foreground/5'
                }
                value={form[key]}
                placeholder={KOLULA_DEFAULTS[key]}
                disabled={locked || saving}
                maxLength={2000}
                onChange={(event) => draft.setDraft({ ...form, [key]: event.target.value })}
            />
        </div>
    );

    /** The texts — locked in the view, editable in the draft. */
    const renderTexts = (locked: boolean) => (
        <div className={'flex flex-col gap-6'}>
            {/* Prominent by requirement (D1 item 5): kolula supplies wording, the
                tenant is the trader and carries the liability. */}
            <p className={'text-sm text-foreground/60 border border-foreground/15 rounded-[5px] p-3'}>
                {t('account.legal_texts.no_legal_advice')}
            </p>

            {TEXTS.map(({ labelKey, hintKey, de, en }) => (
                <div key={labelKey}>
                    <p className={'text-sm font-bold mb-1'}>{t(labelKey)}</p>
                    <p className={'text-xs text-foreground/60 mb-2'}>{t(hintKey)}</p>
                    <div className={'flex flex-col gap-3 xl:flex-row'}>
                        {field(de, 'de', locked)}
                        {field(en, 'en', locked)}
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <EditableSection
            id={'legal-texts'}
            title={t('account.legal_texts.title')}
            description={t('account.legal_texts.intro')}
            readOnly={readOnly}
            editing={draft.editing}
            isDirty={draft.isDirty}
            saving={saving}
            error={error}
            onEdit={() => { setError(null); draft.startEdit(); }}
            onCancel={() => { setError(null); draft.cancel(); }}
            onSave={() => { void save(); }}
            view={renderTexts(true)}
        >
            {renderTexts(false)}
        </EditableSection>
    );
};

/**
 * The legal-texts card as the Account page places it. Who sees and who edits
 * it is `shownLegalTexts` (own module, so a test can reach it without the API
 * layer).
 */
type AccountLegalTextsProps = LegalTextsSectionProps & {
    /** The caller edits their OWN tenant (`!isMaster || ownsViewedTenant`). */
    ownsTenant: boolean;
    isSelfService: boolean;
};

export const AccountLegalTexts = ({ ownsTenant, isSelfService, readOnly, legalTexts }: AccountLegalTextsProps) => {
    const { visible, editable } = shownLegalTexts(ownsTenant, isSelfService);
    if (!visible) return null;
    return <LegalTextsSection readOnly={readOnly || !editable} legalTexts={legalTexts} />;
};

export default LegalTextsSection;
